import type { Agent } from '../agent';
import { ModelBehaviorError } from '../errors';
import type { Handoff } from '../handoff';
import {
  RunHandoffCallItem,
  type RunItem,
  RunMessageOutputItem,
  RunReasoningItem,
  RunToolApprovalItem,
  RunToolCallItem,
  RunToolCallOutputItem,
} from '../items';
import type { ModelResponse } from '../model';
import type { RunState } from '../run-state';
import {
  type FunctionTool,
  getClientToolSearchExecutor,
  type HostedMCPTool,
  type Tool,
} from '../tool';
import {
  getFunctionToolNamespace,
  getFunctionToolQualifiedName,
  getToolCallNamespace,
  resolveFunctionToolCallName,
} from '../tool-identity';
import { addErrorToCurrentSpan } from '../tracing/context';
import type { AgentInputItem } from '../types';
import * as protocol from '../types/protocol';
import * as ProviderData from '../types/provider-data';
import { collectAgentInputItemIds, ensureRunItemIds } from './item-identity';
import { getToolCallOutputItem } from './tool-execution';
import { getClientToolSearchHelper } from './tool-search';
import type {
  ProcessedResponse,
  ToolRunFunction,
  ToolRunHandoff,
  ToolRunMCPApprovalRequest,
} from './types';

export async function processModelResponseAsync<TContext>(
  modelResponse: ModelResponse,
  agent: Agent<any, any>,
  tools: Tool<TContext>[],
  handoffs: Handoff<any, any>[],
  _state: RunState<TContext, Agent<any, any>>,
  priorItems: Array<RunItem | AgentInputItem> = [],
): Promise<ProcessedResponse<TContext>> {
  const clientToolSearchTool = getClientToolSearchHelper(tools);
  const hasCustomClientToolSearchExecutor = Boolean(
    clientToolSearchTool && getClientToolSearchExecutor(clientToolSearchTool),
  );
  const hasRelevantClientToolSearchCall = false;
  if (!hasCustomClientToolSearchExecutor || !hasRelevantClientToolSearchCall) {
    return processModelResponse(
      modelResponse,
      agent,
      tools,
      handoffs,
      priorItems,
    );
  }

  const items: RunItem[] = [];
  const runHandoffs: ToolRunHandoff[] = [];
  const runFunctions: ToolRunFunction<TContext>[] = [];
  const hasHostedShellCall = false;
  const runMCPApprovalRequests: ToolRunMCPApprovalRequest[] = [];
  const toolsUsed: string[] = [];
  const handoffMap = new Map(handoffs.map((h) => [h.toolName, h]));
  const functionMap = buildFunctionToolMap(tools);
  const mcpToolMap = new Map(
    tools
      .filter((t) => t.type === 'hosted_tool' && t.providerData?.type === 'mcp')
      .map((t) => t as HostedMCPTool)
      .map((t) => [t.providerData.server_label, t]),
  );
  const hasGeneratedClientToolSearchOutputs = false;
  let hasUnknownFunctionCall = false;

  for (const output of modelResponse.output) {
    if (output.type === 'message') {
      if (output.role === 'assistant') {
        items.push(new RunMessageOutputItem(output, agent));
      }
      /*} else if (output.type === "tool_search_call") {
      items.push(new RunToolSearchCallItem(output, agent));
      toolsUsed.push("tool_search");
      const generatedOutput =
        generatedClientToolSearchOutputsByCall.get(output);
      if (generatedOutput) {
        items.push(new RunToolSearchOutputItem(generatedOutput.output, agent));
        recordLoadedToolSearchOutput(
          loadedDeferredToolState,
          generatedOutput.output,
        );
        addHostedMcpToolsFromToolSearchOutput(
          generatedOutput.output,
          mcpToolMap,
          {
            preserveExistingServerLabels: originalMcpServerLabels,
          },
        );
        const novelRuntimeTools = registerRuntimeToolSearchTools({
          availableTools,
          functionMap,
          mcpToolMap,
          replaceableRuntimeToolKeys,
          runtimeTools: generatedOutput.runtimeTools,
        });
        state.recordToolSearchRuntimeTools(
          agent,
          generatedOutput.output,
          novelRuntimeTools,
        );
        hasGeneratedClientToolSearchOutputs = true;
      }
    
    } else if (output.type === "tool_search_output") {
      items.push(new RunToolSearchOutputItem(output, agent));
      recordLoadedToolSearchOutput(loadedDeferredToolState, output);
      addHostedMcpToolsFromToolSearchOutput(output, mcpToolMap, {
        preserveExistingServerLabels: originalMcpServerLabels,
      });
*/
    } else if (output.type === 'hosted_tool_call') {
      items.push(new RunToolCallItem(output, agent));
      const toolName = output.name;
      toolsUsed.push(toolName);

      if (
        output.providerData?.type === 'mcp_approval_request' ||
        output.name === 'mcp_approval_request'
      ) {
        const providerData =
          output.providerData as ProviderData.HostedMCPApprovalRequest;

        const mcpServerLabel = providerData.server_label;
        const mcpServerTool = mcpToolMap.get(mcpServerLabel);
        if (typeof mcpServerTool === 'undefined') {
          const message = `MCP server (${mcpServerLabel}) not found in Agent (${agent.name})`;
          addErrorToCurrentSpan({
            message,
            data: { mcp_server_label: mcpServerLabel },
          });
          throw new ModelBehaviorError(message);
        }

        const approvalItem = new RunToolApprovalItem(
          {
            type: 'hosted_tool_call',
            name: providerData.name,
            id: providerData.id,
            status: 'in_progress',
            providerData,
          },
          agent,
        );
        runMCPApprovalRequests.push({
          requestItem: approvalItem,
          mcpTool: mcpServerTool,
        });
        if (!mcpServerTool.providerData.on_approval) {
          items.push(approvalItem);
        }
      }
    } else if (output.type === 'reasoning') {
      items.push(new RunReasoningItem(output, agent));
    }

    if (output.type !== 'function_call') {
      continue;
    }

    const resolved = resolveFunctionOrHandoff(
      output,
      handoffMap,
      functionMap,
      agent,
    );
    if (resolved.type === 'handoff') {
      recordHandoffRequest(
        output,
        resolved.handoff,
        agent,
        items,
        toolsUsed,
        runHandoffs,
      );
    } else if (resolved.type === 'unknown_function') {
      recordUnknownFunctionCall(
        output,
        resolved.toolName,
        agent,
        functionMap,
        items,
        toolsUsed,
      );
      hasUnknownFunctionCall = true;
    } else {
      const normalizedToolCall = normalizeFunctionToolCallForStorage(
        output,
        resolved.tool,
      );
      toolsUsed.push(
        getFunctionToolQualifiedName(resolved.tool) ?? resolved.tool.name,
      );
      items.push(new RunToolCallItem(normalizedToolCall, agent));
      runFunctions.push({
        toolCall: normalizedToolCall,
        tool: resolved.tool,
      });
    }
  }

  ensureRunItemIds(items, {
    usedIds: collectAgentInputItemIds(priorItems),
    mode: 'generated',
  });

  return {
    newItems: items,
    handoffs: runHandoffs,
    functions: runFunctions,
    mcpApprovalRequests: runMCPApprovalRequests,
    toolsUsed,
    hasToolsOrApprovalsToRun(): boolean {
      return (
        runHandoffs.length > 0 ||
        runFunctions.length > 0 ||
        runMCPApprovalRequests.length > 0 ||
        hasHostedShellCall ||
        hasGeneratedClientToolSearchOutputs ||
        hasUnknownFunctionCall
      );
    },
  };
}

/**
 * Walks a raw model response and classifies each item so the runner can schedule follow-up work.
 * Returns both the serializable RunItems (for history/streaming) and the actionable tool metadata.
 */
export function processModelResponse<TContext>(
  modelResponse: ModelResponse,
  agent: Agent<any, any>,
  tools: Tool<TContext>[],
  handoffs: Handoff<any, any>[],
  _priorItems: Array<RunItem | AgentInputItem> = [],
): ProcessedResponse<TContext> {
  const items: RunItem[] = [];
  const runHandoffs: ToolRunHandoff[] = [];
  const runFunctions: ToolRunFunction<TContext>[] = [];
  const hasHostedShellCall = false;
  const runMCPApprovalRequests: ToolRunMCPApprovalRequest[] = [];
  const toolsUsed: string[] = [];
  const handoffMap = new Map(handoffs.map((h) => [h.toolName, h]));
  // Resolve tools upfront so we can look up the concrete handler in O(1) while iterating outputs.
  const functionMap = new Map(
    tools
      .filter((t): t is FunctionTool<TContext> => t.type === 'function')
      .map((t) => [getFunctionToolQualifiedName(t) ?? t.name, t]),
  );
  const mcpToolMap = new Map(
    tools
      .filter((t) => t.type === 'hosted_tool' && t.providerData?.type === 'mcp')
      .map((t) => t as HostedMCPTool)
      .map((t) => [t.providerData.server_label, t]),
  );
  const hasGeneratedClientToolSearchOutputs = false;
  let hasUnknownFunctionCall = false;

  for (const output of modelResponse.output) {
    if (output.type === 'message') {
      if (output.role === 'assistant') {
        items.push(new RunMessageOutputItem(output, agent));
      }
    } else if (output.type === 'hosted_tool_call') {
      items.push(new RunToolCallItem(output, agent));
      const toolName = output.name;
      toolsUsed.push(toolName);

      if (
        output.providerData?.type === 'mcp_approval_request' ||
        output.name === 'mcp_approval_request'
      ) {
        // Hosted remote MCP server's approval process
        const providerData =
          output.providerData as ProviderData.HostedMCPApprovalRequest;

        const mcpServerLabel = providerData.server_label;
        const mcpServerTool = mcpToolMap.get(mcpServerLabel);
        if (typeof mcpServerTool === 'undefined') {
          const message = `MCP server (${mcpServerLabel}) not found in Agent (${agent.name})`;
          addErrorToCurrentSpan({
            message,
            data: { mcp_server_label: mcpServerLabel },
          });
          throw new ModelBehaviorError(message);
        }

        // Do this approval later:
        // We support both onApproval callback (like the Python SDK does) and HITL patterns.
        const approvalItem = new RunToolApprovalItem(
          {
            type: 'hosted_tool_call',
            // We must use this name to align with the name sent from the servers
            name: providerData.name,
            id: providerData.id,
            status: 'in_progress',
            providerData,
          },
          agent,
        );
        runMCPApprovalRequests.push({
          requestItem: approvalItem,
          mcpTool: mcpServerTool,
        });
        if (!mcpServerTool.providerData.on_approval) {
          // When onApproval function exists, it confirms the approval right after this.
          // Thus, this approval item must be appended only for the next turn interruption patterns.
          items.push(approvalItem);
        }
      }
    } else if (output.type === 'reasoning') {
      items.push(new RunReasoningItem(output, agent));
    }
    /*
     * Intentionally skip returning here so function_call processing can still
     * run when output.type matches other tool call types.
     */
    if (output.type !== 'function_call') {
      continue;
    }

    const resolved = resolveFunctionOrHandoff(
      output,
      handoffMap,
      functionMap,
      agent,
    );
    if (resolved.type === 'handoff') {
      recordHandoffRequest(
        output,
        resolved.handoff,
        agent,
        items,
        toolsUsed,
        runHandoffs,
      );
    } else if (resolved.type === 'unknown_function') {
      recordUnknownFunctionCall(
        output,
        resolved.toolName,
        agent,
        functionMap,
        items,
        toolsUsed,
      );
      hasUnknownFunctionCall = true;
    } else {
      const normalizedToolCall = normalizeFunctionToolCallForStorage(
        output,
        resolved.tool,
      );
      toolsUsed.push(
        getFunctionToolQualifiedName(resolved.tool) ?? resolved.tool.name,
      );
      items.push(new RunToolCallItem(normalizedToolCall, agent));
      runFunctions.push({
        toolCall: normalizedToolCall,
        tool: resolved.tool,
      });
    }
  }

  ensureRunItemIds(items, {
    usedIds: collectAgentInputItemIds(_priorItems),
    mode: 'generated',
  });

  return {
    newItems: items,
    handoffs: runHandoffs,
    functions: runFunctions,
    mcpApprovalRequests: runMCPApprovalRequests,
    toolsUsed: toolsUsed,
    hasToolsOrApprovalsToRun(): boolean {
      return (
        runHandoffs.length > 0 ||
        runFunctions.length > 0 ||
        runMCPApprovalRequests.length > 0 ||
        hasHostedShellCall ||
        hasGeneratedClientToolSearchOutputs ||
        hasUnknownFunctionCall
      );
    },
  };
}

function buildFunctionToolMap<TContext>(
  tools: Tool<TContext>[],
): Map<string, FunctionTool<TContext>> {
  return new Map(
    tools
      .filter((t): t is FunctionTool<TContext> => t.type === 'function')
      .map((t) => [getFunctionToolQualifiedName(t) ?? t.name, t]),
  );
}

function resolveFunctionOrHandoff(
  toolCall: protocol.FunctionCallItem,
  handoffMap: Map<string, Handoff<any, any>>,
  functionMap: Map<string, FunctionTool<any>>,
  agent: Agent<any, any>,
):
  | { type: 'handoff'; handoff: Handoff<any, any> }
  | { type: 'function'; tool: FunctionTool<any> }
  | { type: 'unknown_function'; toolName: string } {
  const resolvedToolName =
    resolveFunctionToolCallName(toolCall, functionMap) ?? toolCall.name;
  const namespace = getToolCallNamespace(toolCall);
  if (!namespace && typeof resolvedToolName === 'string') {
    const functionTool = functionMap.get(resolvedToolName);
    const handoff = handoffMap.get(toolCall.name);
    if (functionTool && handoff && resolvedToolName.includes('.')) {
      const message = `Ambiguous dotted tool call ${resolvedToolName} in agent ${agent.name}: it matches both a namespaced function tool and a handoff. Rename one of them or emit the function call with explicit namespace metadata.`;
      addErrorToCurrentSpan({
        message,
        data: {
          tool_name: resolvedToolName,
          agent_name: agent.name,
        },
      });
      throw new ModelBehaviorError(message);
    }

    if (functionTool && resolvedToolName.includes('.')) {
      return { type: 'function', tool: functionTool };
    }

    if (handoff) {
      return { type: 'handoff', handoff };
    }
  }

  const functionTool = functionMap.get(resolvedToolName);
  if (!functionTool) {
    // The model hallucinated a tool name. Rather than aborting the whole turn,
    // we treat this like an invalid tool call (mirroring argument parse errors):
    // emit an error tool result so the model can self-correct on the next turn.
    return { type: 'unknown_function', toolName: resolvedToolName };
  }
  return { type: 'function', tool: functionTool };
}

function recordHandoffRequest(
  output: protocol.FunctionCallItem,
  handoff: Handoff<any, any>,
  agent: Agent<any, any>,
  items: RunItem[],
  toolsUsed: string[],
  handoffs: ToolRunHandoff[],
): void {
  toolsUsed.push(output.name);
  const isPrimaryHandoff = handoffs.length === 0;
  handoffs.push({
    toolCall: output,
    handoff,
  });
  if (!isPrimaryHandoff) {
    return;
  }
  // Only persist the first handoff request. Later handoffs are SDK-ignored and
  // would otherwise bias future turns against valid delegation targets.
  items.push(new RunHandoffCallItem(output, agent));
}

/**
 * Records a call to a tool the agent does not expose. Instead of aborting the
 * turn, we attach an error tool result (mirroring argument parse errors) so the
 * provider transcript stays balanced and the model can retry with a valid tool.
 */
function recordUnknownFunctionCall(
  toolCall: protocol.FunctionCallItem,
  toolName: string,
  agent: Agent<any, any>,
  functionMap: Map<string, FunctionTool<any>>,
  items: RunItem[],
  toolsUsed: string[],
): void {
  const availableTools = [...functionMap.keys()];
  const availableList =
    availableTools.length > 0 ? availableTools.join(', ') : '(none)';
  const errorMessage =
    `Tool ${toolName} is not a registered tool for agent ${agent.name}. ` +
    `Available tools: ${availableList}. ` +
    `Please retry using one of the available tools.`;
  addErrorToCurrentSpan({
    message: `Tool ${toolName} not found in agent ${agent.name}.`,
    data: { tool_name: toolName, agent_name: agent.name },
  });
  toolsUsed.push(toolName);
  items.push(new RunToolCallItem(toolCall, agent));
  items.push(
    new RunToolCallOutputItem(
      getToolCallOutputItem(toolCall, errorMessage, { isError: true }),
      agent,
      errorMessage,
    ),
  );
}

function normalizeFunctionToolCallForStorage(
  toolCall: protocol.FunctionCallItem,
  tool: FunctionTool<any>,
): protocol.FunctionCallItem {
  const namespace = getToolCallNamespace(toolCall);
  const explicitNamespace = getFunctionToolNamespace(tool);
  const qualifiedToolName = getFunctionToolQualifiedName(tool);

  if (
    namespace ||
    !explicitNamespace ||
    !qualifiedToolName ||
    toolCall.name !== qualifiedToolName
  ) {
    return toolCall;
  }

  return {
    ...toolCall,
    name: tool.name,
    namespace: explicitNamespace,
  };
}
